/**
 * Flow validation utilities - Consolidated validation logic
 */

import { getComponentMaxPerScreen, validateComponent } from './componentUtils';
import { switchRouting } from './routingSwitch';

/**
 * Validation levels
 */
export const VALIDATION_LEVELS = {
  BASIC: 'basic',
  STRICT: 'strict',
  DEPLOYMENT: 'deployment'
};

/**
 * The Flow JSON version every flow we author declares (ADR-0005).
 *
 * Single constant on purpose: the serializer and the pre-deployment check used
 * to carry independent literals and drifted (the serializer emitted 7.3 while
 * the deployment check still warned about anything that was not 7.1, so every
 * flow we produced warned about itself).
 *
 * Distinct from the message-level `flow_message_version` ("3").
 */
export const FLOW_JSON_VERSION = '7.3';

/**
 * WhatsApp caps a screen's layout at 50 children.
 */
export const MAX_COMPONENTS_PER_SCREEN = 50;

/** WhatsApp rejects an If nested deeper than this: "can only be nested up to 3 levels deep". */
export const MAX_IF_DEPTH = 3;

/**
 * A condition WhatsApp's parser refuses: a comparison followed by `||` or `&&`. Probed on
 * 2026-09-18: `a == 'x' || a == 'y'`, with or without parentheses, is "Wrong positioning of
 * operator" or a type mismatch. One comparison per condition; many-way choices route with rules.
 */
export const COMBINED_COMPARISON = /(==|!=|>=|<=|>|<)[^|&]*(\|\||&&)/;

/**
 * Components whose visible text is a plain string WhatsApp prints as it stands.
 *
 * `${data.x}` is a different thing and stays allowed: a data-bound screen declares it and the
 * endpoint supplies it (see booking.js), which is the one way text can carry a value.
 */
export const TEXT_COMPONENTS = ['TextHeading', 'TextSubheading', 'TextBody', 'TextCaption', 'RichText'];

/**
 * An answer referenced inside visible text: "Day: ${screen.DATE.form.date}".
 *
 * A "check and confirm" screen is the tempting place for this, and it does not work. Meta's own
 * validator is no help, it does not resolve references in text at all, and accepts one pointing
 * at a screen that does not exist (probed 2026-09-20), so the flow deploys and the customer is
 * shown the code where their answer should be.
 */
export const VARIABLE_IN_TEXT = /\$\{\s*(screen|form)\./;

/**
 * Every action in a list of Flow JSON components, containers included.
 *
 * A component carries one `on-click-action`; a NavigationList carries one per list item. There
 * is no other action property in Flow JSON 7.3, so this is the whole set.
 */
export const actionsIn = (children) => (Array.isArray(children) ? children : []).flatMap((child) => {
  if (!child || typeof child !== 'object') return [];
  const own = [child['on-click-action'], ...((child['list-items'] || []).map((item) => item && item['on-click-action']))]
    .filter((action) => action && typeof action === 'object');
  if (child.type === 'If') return [...own, ...actionsIn(child.then), ...actionsIn(child.else)];
  if (child.type === 'Switch') return [...own, ...Object.values(child.cases || {}).flatMap(actionsIn)];
  return [...own, ...actionsIn(child.children)];
});

/** Every screen a Footer or NavigationList item in a list of Flow JSON components navigates to, containers included. */
export const navigateTargetsIn = (children) => actionsIn(children)
  .filter((action) => action.name === 'navigate' && action.next && action.next.name)
  .map((action) => action.next.name);

/**
 * An action's `payload` has to be a JSON object.
 *
 * Meta calls it a "static JSON payload": a map of names to values, where a value may be a
 * `${...}` reference. Handing it a bare reference string instead, `"${data.whatever}"`, meaning
 * "return that object whole", is refused at deploy with
 * `Expected property 'payload' to be of type 'object' but found 'string'`.
 *
 * Found on 2026-09-23, from a booking flow that had carried
 * `payload: "${data.extension_message_response}"` since the template was written, in the belief
 * that it was Meta's documented terminal-screen form. It is not: the endpoint's
 * `extension_message_response` reaches the business on its own, and Meta's own sample terminal
 * screen completes with `payload: {}`. The flow deployed, synced, and was INVALID at Meta while
 * every check here passed.
 */
export const payloadProblemsIn = (children) => actionsIn(children)
  .filter((action) => 'payload' in action)
  .filter((action) => action.payload === null
    || typeof action.payload !== 'object'
    || Array.isArray(action.payload))
  .map((action) => `The "${action.name || 'unnamed'}" action carries a ${payloadTypeName(action.payload)} payload, `
    + 'and WhatsApp requires an object. Write it as named fields, '
    + 'for example {"booking_id": "${data.booking_id}"}.');

const payloadTypeName = (payload) => {
  if (payload === null) return 'null';
  if (Array.isArray(payload)) return 'list';
  return typeof payload;
};

/** Every piece of visible text in a list of Flow JSON components, containers included. */
export const textsIn = (children) => (Array.isArray(children) ? children : []).flatMap((child) => {
  if (!child || typeof child !== 'object') return [];
  const own = TEXT_COMPONENTS.includes(child.type) && typeof child.text === 'string' ? [child.text] : [];
  if (child.type === 'If') return [...own, ...textsIn(child.then), ...textsIn(child.else)];
  if (child.type === 'Switch') return [...own, ...Object.values(child.cases || {}).flatMap(textsIn)];
  return [...own, ...textsIn(child.children)];
});

/** Every `condition` string in a list of Flow JSON components, containers included. */
export const conditionsIn = (children) => (Array.isArray(children) ? children : []).flatMap((child) => {
  if (!child || typeof child !== 'object') return [];
  const own = typeof child.condition === 'string' ? [child.condition] : [];
  if (child.type === 'If') return [...own, ...conditionsIn(child.then), ...conditionsIn(child.else)];
  if (child.type === 'Switch') return Object.values(child.cases || {}).flatMap(conditionsIn);
  return [...own, ...conditionsIn(child.children)];
});

/**
 * Switch case keys Meta's validator can read: letters, digits, `_` and `-`.
 *
 * A key with a space or a bracket ("MOT (45 min)") does not get a validation message from Meta,
 * it crashes the validator: "Flow JSON is not valid. Cannot read property 'type' of undefined",
 * at no path. Probed against the Graph API on 2026-10-09 (evals/meta-validate.sh): spaces and
 * brackets fail; letters, digits, underscores and hyphens pass. If conditions are not affected,
 * `${form.job} == 'MOT (45 min)'` is fine, so a readable answer can still be branched on with Ifs.
 */
export const SWITCH_CASE_KEY = /^[A-Za-z0-9_-]+$/;

/** Every Switch case key in a list of Flow JSON components, containers included. */
export const switchCaseKeysIn = (children) => (Array.isArray(children) ? children : []).flatMap((child) => {
  if (!child || typeof child !== 'object') return [];
  if (child.type === 'If') return [...switchCaseKeysIn(child.then), ...switchCaseKeysIn(child.else)];
  if (child.type === 'Switch') {
    const cases = child.cases || {};
    return [...Object.keys(cases), ...Object.values(cases).flatMap(switchCaseKeysIn)];
  }
  return switchCaseKeysIn(child.children);
});

/**
 * A range CalendarPicker describes each end separately: label, helper-text, error-message and
 * required must each be {"start-date", "end-date"}, and Meta refuses a plain string or boolean.
 * Probed 2026-10-10 (preview parity audit): "The value of property 'label' of component
 * 'CalendarPicker' must be of type {start-date: string, end-date: string}", and the same for the
 * other three.
 */
export const RANGE_PAIRED = ['label', 'helper-text', 'error-message', 'required'];

/** Every range-mode CalendarPicker in a list of Flow JSON components, containers included. */
export const rangeCalendarsIn = (children) => (Array.isArray(children) ? children : []).flatMap((child) => {
  if (!child || typeof child !== 'object') return [];
  const own = child.type === 'CalendarPicker' && child.mode === 'range' ? [child] : [];
  if (child.type === 'If') return [...own, ...rangeCalendarsIn(child.then), ...rangeCalendarsIn(child.else)];
  if (child.type === 'Switch') return [...own, ...Object.values(child.cases || {}).flatMap(rangeCalendarsIn)];
  return [...own, ...rangeCalendarsIn(child.children)];
});

/**
 * WhatsApp writes "(optional)" after the label of an upload nobody has to make, so a label that
 * already says it reads "Referral letter (optional) (optional)" on the phone. Seen on a real
 * phone on 2026-10-10 with the clinic demo's DocumentPicker (min-uploaded-documents 0). A
 * warning, not an error: the flow works, it just looks careless.
 */
export const SAYS_OPTIONAL = /\(\s*optional\s*\)\s*$/i;

/**
 * The questions WhatsApp marks "(optional)" when they are not required, after the label (before
 * it on an OptIn). Seen in Meta's preview of the parity reference flows on 2026-10-10 for every
 * one of these; a single-date CalendarPicker included, a range one labels its ends instead.
 */
export const MARKED_OPTIONAL = ['TextInput', 'TextArea', 'Dropdown', 'DatePicker', 'CalendarPicker',
  'RadioButtonsGroup', 'CheckboxGroup', 'ChipsSelector', 'OptIn'];

const SAYS_OPTIONAL_FIRST = /^\s*\(\s*optional\s*\)/i;

/** Every optional question in a list of Flow JSON components whose label already says so. */
export const doubledOptionalIn = (children) => (Array.isArray(children) ? children : []).flatMap((child) => {
  if (!child || typeof child !== 'object') return [];
  if (child.type === 'If') return [...doubledOptionalIn(child.then), ...doubledOptionalIn(child.else)];
  if (child.type === 'Switch') return Object.values(child.cases || {}).flatMap(doubledOptionalIn);
  const marked = MARKED_OPTIONAL.includes(child.type) && child.required !== true
    && !(child.type === 'CalendarPicker' && child.mode === 'range');
  const says = typeof child.label === 'string'
    && (SAYS_OPTIONAL.test(child.label) || (child.type === 'OptIn' && SAYS_OPTIONAL_FIRST.test(child.label)));
  return [...(marked && says ? [child] : []), ...doubledOptionalIn(child.children)];
});

/** True when WhatsApp itself will mark this picker optional: no minimum number of uploads. */
export const pickerIsOptional = (picker) =>
  !Number(picker['min-uploaded-documents'] ?? picker['min-uploaded-photos'] ?? 0);

/**
 * Meta's hard limit on CTA button text.
 *
 * Enforced by FooterCore for the builder's own model, and, until 2026-09-24, by nothing at all
 * on the Flow JSON path. The two validators had drifted: the model knew the rule, the JSON did
 * not, and the JSON is what the backend's save gate and every generated flow go through. The
 * plumber-quote example shipped with a 21-character label ("Send my quote request") and was
 * pronounced valid by `validateFlowJson` right up until the builder hydrated it and said
 * otherwise.
 */
export const MAX_FOOTER_LABEL = 20;

/** Every Footer label in a list of Flow JSON components, containers included. */
export const footerLabelsIn = (children) => (Array.isArray(children) ? children : []).flatMap((child) => {
  if (!child || typeof child !== 'object') return [];
  const own = child.type === 'Footer' && typeof child.label === 'string' ? [child.label] : [];
  if (child.type === 'If') return [...own, ...footerLabelsIn(child.then), ...footerLabelsIn(child.else)];
  if (child.type === 'Switch') return [...own, ...Object.values(child.cases || {}).flatMap(footerLabelsIn)];
  return [...own, ...footerLabelsIn(child.children)];
});

/** What WhatsApp accepts as a screen id: letters and underscores, nothing else (no digits). */
export const SCREEN_ID_PATTERN = /^[A-Za-z_]+$/;

/** How deep If components nest in a list of Flow JSON components (0 when there is none). */
export const ifNestingDepth = (children) => Math.max(0, ...(Array.isArray(children) ? children : []).map((child) => {
  if (!child || typeof child !== 'object') return 0;
  if (child.type === 'If') return 1 + Math.max(ifNestingDepth(child.then), ifNestingDepth(child.else));
  if (child.type === 'Switch') return Math.max(0, ...Object.values(child.cases || {}).map(ifNestingDepth));
  return ifNestingDepth(child.children);
}));

/**
 * Walk a builder component and every nested child inside container branches
 * (If.then/else, Switch.cases[].children), calling `visit` on each.
 *
 * Single shared traversal on purpose: every validation entry point must see the
 * same set of components. Before this existed, buildValidationMap recursed into
 * containers but validateBuilderScreens (the save/export/deploy gate) did not,
 * so a broken component nested in an If passed the gate and failed at Meta.
 */
export const forEachComponentDeep = (component, visit) => {
  if (!component) return;
  visit(component);
  if (component.type === 'If') {
    (component.properties?.then || []).forEach((child) => forEachComponentDeep(child, visit));
    (component.properties?.else || []).forEach((child) => forEachComponentDeep(child, visit));
  } else if (component.type === 'Switch') {
    (component.properties?.cases || []).forEach((branch) => {
      (branch?.children || []).forEach((child) => forEachComponentDeep(child, visit));
    });
  }
};

/** forEachComponentDeep applied to every component of every screen. */
export const forEachComponentInScreens = (screens, visit) => {
  (Array.isArray(screens) ? screens : []).forEach((screen) => {
    if (Array.isArray(screen.components)) {
      screen.components.forEach((component) => forEachComponentDeep(component, visit));
    }
  });
};

/**
 * Validate flow JSON structure
 */
export const validateFlowStructure = (flowJson) => {
  const errors = [];
  
  // Check required fields
  if (!flowJson.version) {
    errors.push('Version is required');
  }
  
  if (!flowJson.screens || !Array.isArray(flowJson.screens)) {
    errors.push('Screens array is required');
  }
  
  return errors;
};

/**
 * Meta's rule for RichText: it cannot share a screen with anything but a Footer. Neither
 * per-type counts nor property validation can see it, so it is checked here for both the
 * builder model and Flow JSON, with one wording so the repair turn gets it once.
 */
export const RICH_TEXT_ALONE_ERROR = "RichText must be the only component on its screen (a Footer may follow it)";
const richTextShareViolation = (components) => {
  const types = (components || []).map((component) => component && component.type);
  return types.includes('RichText') && types.some((type) => type !== 'RichText' && type !== 'Footer');
};

/**
 * Validate individual screen
 */
export const validateScreen = (screen, screenIndex, level = VALIDATION_LEVELS.BASIC) => {
  const errors = [];
  const warnings = [];
  // Same wording as the Flow JSON validator's screen label, so a finding both make ("too many
  // RichText components") dedupes to one line for the repair turn instead of two.
  const label = screen.title ? `Screen "${screen.title}"` : `Screen ${screenIndex + 1}`;
  
  if (!screen.id) {
    errors.push(`${label} is missing an ID`);
  }
  
  if (!screen.layout) {
    errors.push(`${label} is missing layout`);
  }
  
  if (screen.layout && !screen.layout.children) {
    errors.push(`${label} layout is missing children`);
  }
  
  // Component validation
  if (screen.layout && screen.layout.children) {
    const components = screen.layout.children;
    
    // Check component limits
    if (components.length > MAX_COMPONENTS_PER_SCREEN) {
      errors.push(`${label} has too many components (max ${MAX_COMPONENTS_PER_SCREEN})`);
    }
    
    // Check component-specific limits
    const componentCounts = {};
    components.forEach(comp => {
      componentCounts[comp.type] = (componentCounts[comp.type] || 0) + 1;
    });
    
    Object.entries(componentCounts).forEach(([type, count]) => {
      const maxPerScreen = getComponentMaxPerScreen(type);
      if (maxPerScreen && count > maxPerScreen) {
        errors.push(`${label} has too many ${type} components (max ${maxPerScreen})`);
      }
    });
    if (richTextShareViolation(components)) {
      errors.push(`${label}, ${RICH_TEXT_ALONE_ERROR}`);
    }
    
    // Strict validation
    if (level === VALIDATION_LEVELS.STRICT || level === VALIDATION_LEVELS.DEPLOYMENT) {
      // Check for required component properties
      components.forEach((component, compIndex) => {
        if (component.type === 'EmbeddedLink' && !component.url && !component['on-click-action']) {
          errors.push(`EmbeddedLink component ${compIndex + 1} in screen ${screenIndex + 1} is missing URL or action`);
        }
        
        if (['CheckboxGroup', 'RadioButtonsGroup', 'Dropdown'].includes(component.type)) {
          const dataSource = component['data-source'];
          // A `${data.x}` string is a dynamic binding (options come from the
          // data-exchange endpoint at runtime), valid without static options.
          const isDynamicBinding = typeof dataSource === 'string' && /^\$\{data\..+\}$/.test(dataSource);
          if (!isDynamicBinding && (!dataSource || !Array.isArray(dataSource) || dataSource.length === 0)) {
            errors.push(`${component.type} component ${compIndex + 1} in screen ${screenIndex + 1} needs at least one option`);
          }
        }
      });
    }
    
    // Deployment validation
    if (level === VALIDATION_LEVELS.DEPLOYMENT) {
      // Check for navigation flow
      const hasFooter = components.some(comp => comp.type === 'Footer');
      if (!hasFooter && !screen.terminal) {
        warnings.push(`${label} should have a Footer component for navigation`);
      }
    }
  }
  
  return { errors, warnings };
};

/**
 * Validate complete flow
 */
/** Meta's ceilings on what a completed flow may carry back. */
const MAX_UPLOADED_PHOTOS = 10;
const MAX_UPLOADED_KB = 102400;

/** The two components that upload a file. Meta's limits treat them as one thing. */
export const MEDIA_PICKERS = ['PhotoPicker', 'DocumentPicker'];

/** How many files a picker may take, and how big, Meta's defaults when the flow says nothing. */
const filesAllowed = (picker) => (picker.type === 'PhotoPicker'
  ? picker['max-uploaded-photos']
  : picker['max-uploaded-documents']) || 30;

/** Every PhotoPicker and DocumentPicker in a list of components, containers included. */
export const mediaPickersIn = (children) => (Array.isArray(children) ? children : []).flatMap((child) => {
  if (!child || typeof child !== 'object') return [];
  const own = MEDIA_PICKERS.includes(child.type) ? [child] : [];
  if (child.type === 'If') return [...own, ...mediaPickersIn(child.then), ...mediaPickersIn(child.else)];
  if (child.type === 'Switch') return [...own, ...Object.values(child.cases || {}).flatMap(mediaPickersIn)];
  return [...own, ...mediaPickersIn(child.children)];
});

/**
 * Uploads are counted across the WHOLE flow, and photos and documents share one budget.
 *
 * Meta rejects a published flow whose response message could carry more than ten files, or more
 * than 102400 KB of them, "images OR documents" added together. The compiler caps each picker so
 * one or two are always safe; this catches the flow that has three, and says so in our own words
 * rather than letting Meta say it at publish time, in front of whoever was being shown the
 * product.
 *
 * Counted across both types since 2026-09-24, when DocumentPicker was added: before that a flow
 * could pair a full PhotoPicker with a full DocumentPicker and pass, because nothing added them
 * up.
 */
const validateMediaBudget = (flowJson) => {
  const pickers = (flowJson.screens || [])
    .flatMap((screen) => mediaPickersIn(screen.layout && screen.layout.children));

  if (pickers.length === 0) return [];

  const files = pickers.reduce((total, p) => total + filesAllowed(p), 0);
  const kb = pickers.reduce((total, p) => total + filesAllowed(p) * (p['max-file-size-kb'] || 25600), 0);

  const errors = [];
  if (files > MAX_UPLOADED_PHOTOS) {
    errors.push(`Uploads across the flow total ${files} files, and WhatsApp allows `
      + `${MAX_UPLOADED_PHOTOS}. Lower the maximum on a picker, or use fewer upload questions.`);
  }
  if (kb > MAX_UPLOADED_KB) {
    errors.push(`Uploads across the flow could total ${kb} KB, and WhatsApp allows `
      + `${MAX_UPLOADED_KB} KB. Lower the maximum on a picker, or its file size limit.`);
  }
  return errors;
};

/**
 * One upload question per screen, counting both kinds.
 *
 * Meta: "You can only have a maximum of 1 component of type PhotoPicker or DocumentPicker per
 * screen." A per-component `maxPerScreen` cannot express this, it would let one of each share a
 * screen, which Meta refuses.
 */
const validateOnePickerPerScreen = (flowJson) => (flowJson.screens || []).flatMap((screen) => {
  const children = (screen.layout && screen.layout.children) || [];
  const pickers = mediaPickersIn(children);
  if (pickers.length < 2) return [];

  // Two of the SAME type sitting directly on the screen is already reported by that component's
  // own maxPerScreen, and saying it twice in different words helps nobody. That rule counts only
  // TOP-LEVEL children though, so a picker tucked inside an If is invisible to it, and a mixed
  // pair is invisible to it by definition. Those are the cases this exists for.
  const topLevelCounts = {};
  children.forEach((child) => {
    if (child && MEDIA_PICKERS.includes(child.type)) {
      topLevelCounts[child.type] = (topLevelCounts[child.type] || 0) + 1;
    }
  });
  if (Object.values(topLevelCounts).some((count) => count > 1)) return [];

  const label = screen.title || screen.id;
  const types = new Set(pickers.map((picker) => picker.type));
  return [types.size > 1
    ? `Screen "${label}" asks for both photos and documents; WhatsApp allows one upload question `
      + 'per screen, of either kind. Move one to its own screen.'
    : `Screen "${label}" has ${pickers.length} upload questions, counting the ones inside a `
      + 'condition; WhatsApp allows one per screen. Move one to its own screen.'];
});

/**
 * Where an uploaded file may be referenced, which is narrower than it looks.
 *
 * Meta refuses a picker's value in a `navigate` payload at all, and inside `data_exchange` or
 * `complete` it has to be the value of a TOP-LEVEL property, `{"media": "${form.doc}"}` is
 * fine, `{"media": {"doc": "${form.doc}"}}` is not. Both are publish-time refusals with no
 * warning at deploy, so they are worth catching here.
 */
const validatePickerReferences = (flowJson) => {
  const names = (flowJson.screens || [])
    .flatMap((screen) => mediaPickersIn(screen.layout && screen.layout.children))
    .map((picker) => picker.name)
    .filter(Boolean);
  if (names.length === 0) return [];

  const mentionsPicker = (value) => typeof value === 'string'
    && names.some((name) => value.includes(`form.${name}}`));

  const buriedPicker = (value) => {
    if (Array.isArray(value)) return value.some((item) => mentionsPicker(item) || buriedPicker(item));
    if (value && typeof value === 'object') {
      return Object.values(value).some((item) => mentionsPicker(item) || buriedPicker(item));
    }
    return false;
  };

  return (flowJson.screens || []).flatMap((screen) => {
    const label = screen.title || screen.id;
    return actionsIn(screen.layout && screen.layout.children).flatMap((action) => {
      const payload = action.payload;
      if (!payload || typeof payload !== 'object') return [];
      const values = Object.values(payload);
      const errors = [];
      if (action.name === 'navigate' && (values.some(mentionsPicker) || values.some(buriedPicker))) {
        errors.push(`Screen "${label}" passes an uploaded file to the next screen. WhatsApp does `
          + 'not allow that on a Continue button, the file can only be sent when the flow submits.');
      }
      if (action.name !== 'navigate' && values.some(buriedPicker)) {
        errors.push(`Screen "${label}" nests an uploaded file inside another value. WhatsApp `
          + 'requires it to be a field of its own in what the flow sends back.');
      }
      return errors;
    });
  });
};

export const validateFlow = (flowJson, level = VALIDATION_LEVELS.BASIC) => {
  const errors = [];
  const warnings = [];
  
  // Structure validation
  errors.push(...validateFlowStructure(flowJson));
  
  // Screen validation
  if (flowJson.screens) {
    flowJson.screens.forEach((screen, index) => {
      const screenValidation = validateScreen(screen, index, level);
      errors.push(...screenValidation.errors);
      warnings.push(...screenValidation.warnings);
      // Meta's rule, found the hard way on 2026-09-18: a routing chain four rules long compiled to
      // four nested Ifs, passed every check here and was refused at deploy.
      const depth = ifNestingDepth(screen?.layout?.children);
      if (depth > MAX_IF_DEPTH) {
        errors.push(`Screen "${screen.title || screen.id}" nests If conditions ${depth} levels deep; WhatsApp allows ${MAX_IF_DEPTH}.`);
      }
      // Meta: "Property 'id' should only consist of alphabets and underscores" (2026-09-18).
      if (typeof screen?.id === 'string' && !SCREEN_ID_PATTERN.test(screen.id)) {
        errors.push(`Screen id "${screen.id}" may only contain letters and underscores.`);
      }
      if (navigateTargetsIn(screen?.layout?.children).includes(screen?.id)) {
        errors.push(`Screen "${screen.title || screen.id}" navigates to itself, which WhatsApp does not allow (loop detected).`);
      }
      footerLabelsIn(screen?.layout?.children).forEach((label) => {
        if (label.length > MAX_FOOTER_LABEL) {
          errors.push(`Screen "${screen.title || screen.id}" has a button labelled "${label}" (${label.length} characters); WhatsApp allows ${MAX_FOOTER_LABEL}.`);
        }
      });
      payloadProblemsIn(screen?.layout?.children).forEach((problem) => {
        errors.push(`Screen "${screen.title || screen.id}", ${problem}`);
      });
      conditionsIn(screen?.layout?.children).forEach((condition) => {
        if (COMBINED_COMPARISON.test(condition)) {
          errors.push(`Screen "${screen.title || screen.id}" has a condition WhatsApp cannot parse (a comparison followed by || or &&): ${condition}. Use one comparison per condition.`);
        }
      });
      switchCaseKeysIn(screen?.layout?.children).forEach((key) => {
        if (!SWITCH_CASE_KEY.test(key)) {
          errors.push(`Screen "${screen.title || screen.id}" has a Switch case "${key}"; WhatsApp only accepts case values made of letters, digits, _ and -. Use a plain value such as "${key.replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '')}", or branch with If conditions instead.`);
        }
      });
      rangeCalendarsIn(screen?.layout?.children).forEach((calendar) => {
        RANGE_PAIRED.filter((key) => calendar[key] !== undefined
          && (typeof calendar[key] !== 'object' || calendar[key] === null
            || !('start-date' in calendar[key]) || !('end-date' in calendar[key])))
          .forEach((key) => {
            errors.push(`Screen "${screen.title || screen.id}" has a date range "${calendar.name}" whose ${key} is one value. WhatsApp needs one for each end: {"start-date": ..., "end-date": ...}.`);
          });
      });
      doubledOptionalIn(screen?.layout?.children).forEach((question) => {
        const plain = question.label.replace(SAYS_OPTIONAL, '').replace(SAYS_OPTIONAL_FIRST, '').trim();
        warnings.push(`Screen "${screen.title || screen.id}" labels a question "${question.label}". WhatsApp adds "(optional)" to a question that is not required, so it will show twice. Use "${plain}".`);
      });
      mediaPickersIn(screen?.layout?.children).forEach((picker) => {
        if (pickerIsOptional(picker) && typeof picker.label === 'string' && SAYS_OPTIONAL.test(picker.label)) {
          warnings.push(`Screen "${screen.title || screen.id}" labels an upload "${picker.label}". WhatsApp adds "(optional)" to an upload that is not required, so it will show twice. Use "${picker.label.replace(SAYS_OPTIONAL, '').trim()}".`);
        }
      });
      textsIn(screen?.layout?.children).forEach((text) => {
        if (VARIABLE_IN_TEXT.test(text)) {
          errors.push(`Screen "${screen.title || screen.id}" prints an answer back as text: ${text.trim()}. WhatsApp does not fill those in, so the customer sees the code itself. Take the line out, or ask the question rather than repeating it.`);
        }
      });
    });
    
    // Flow-level validation
    if (level === VALIDATION_LEVELS.DEPLOYMENT) {
      if (flowJson.screens.length === 0) {
        errors.push('Flow must have at least one screen');
      }
      
      // Check for terminal screen
      const hasTerminalScreen = flowJson.screens.some(screen => screen.terminal);
      if (!hasTerminalScreen) {
        warnings.push('Flow should have at least one terminal screen');
      }

      errors.push(...validateMediaBudget(flowJson));
      errors.push(...validateOnePickerPerScreen(flowJson));
      errors.push(...validatePickerReferences(flowJson));
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Validate flow for deployment
 */
export const validateForDeployment = (flowJson) => {
  return validateFlow(flowJson, VALIDATION_LEVELS.DEPLOYMENT);
};

/**
 * Validate flow builder screens (internal format)
 */
// WhatsApp allows at most 3 ImageCarousel components in a whole flow (the
// per-screen limit of 2 is handled via maxPerScreen like other components).
const MAX_IMAGE_CAROUSELS_PER_FLOW = 3;

const countImageCarouselsInFlow = (screens) => {
  let count = 0;
  forEachComponentInScreens(screens, (component) => {
    if (component.type === 'ImageCarousel') count += 1;
  });
  return count;
};

/**
 * The checks that belong to the flow as a whole rather than to one component: every screen is
 * reachable from the first, and every routing rule is one WhatsApp accepts (at most MAX_IF_DEPTH
 * deep, one comparison each, never back to the same screen, never to a screen that is not there).
 *
 * Part of validateBuilderScreens (the save gate and the backend's validateModel), and exported on
 * its own because buildValidationMap only holds findings it can pin to a component or screen: the
 * builders showed "No problems" for a flow the save gate and WhatsApp would both refuse
 * (2026-09-28).
 *
 * @returns {{errors: string[], warnings: string[]}}
 */
export const validateScreenGraph = (screens) => {
  const errors = [];
  const warnings = [];
  if (!Array.isArray(screens)) return { errors, warnings };

  // BUG-005: Block export when screens are disconnected.
  // Build a reachability graph from the first screen using Footer and
  // NavigationList navigate actions, then flag any unreachable screens.
  if (screens.length > 1) {
    const screenIds = new Set(screens.map(s => s.id));
    const firstScreenId = screens[0].id;
    const reachable = new Set([firstScreenId]);
    const queue = [firstScreenId];

    while (queue.length > 0) {
      const currentId = queue.shift();
      const currentScreen = screens.find(s => s.id === currentId);
      if (!currentScreen) continue;

      // Follow conditional routing rule targets and custom default next screen
      if (currentScreen.routing) {
        (currentScreen.routing.rules || []).forEach(rule => {
          if (rule.nextScreenId && screenIds.has(rule.nextScreenId) && !reachable.has(rule.nextScreenId)) {
            reachable.add(rule.nextScreenId);
            queue.push(rule.nextScreenId);
          }
        });
        const def = currentScreen.routing.defaultNextScreenId;
        if (def && screenIds.has(def) && !reachable.has(def)) {
          reachable.add(def);
          queue.push(def);
        }
      }

      currentScreen.components.forEach(comp => {
        const targets = [];

        // A data_exchange hands the choice of the next screen to the endpoint, which may
        // return ANY screen in the flow, that is the whole point of a dynamic flow. So it
        // reaches all of them. Without this, every screen of a dynamic flow after the first
        // exchange reads as unreachable, which is a false alarm about the one navigation
        // style this validator cannot see.
        const followsExchange = (action) => action?.name === 'data_exchange';

        if (comp.type === 'Footer') {
          const action = comp.properties?.['on-click-action'];
          // A routing default replaces the Footer's own target at compile time (the Footer
          // chain is rebuilt from the rules), so the list-order target it carries in the
          // model is not where the customer goes. Following it hid an unreachable screen
          // on 2026-09-18: rule and default both led elsewhere and nothing led to it.
          const overriddenByRouting = Boolean(currentScreen.routing?.defaultNextScreenId);
          if (!overriddenByRouting && action?.name === 'navigate' && action?.next?.name) {
            targets.push(action.next.name);
          }
          if (followsExchange(action)) {
            targets.push(...screenIds);
          }
        }

        if (comp.type === 'NavigationList') {
          const items = comp.properties?.['list-items'] || [];
          items.forEach(item => {
            const action = item['on-click-action'];
            if (action?.name === 'navigate' && action?.next?.name) {
              targets.push(action.next.name);
            }
            if (followsExchange(action)) {
              targets.push(...screenIds);
            }
          });
        }

        targets.forEach(target => {
          if (screenIds.has(target) && !reachable.has(target)) {
            reachable.add(target);
            queue.push(target);
          }
        });
      });
    }

    screens.forEach(screen => {
      if (!reachable.has(screen.id)) {
        const screenLabel = screen.title || screen.id;
        errors.push(
          `Screen "${screenLabel}" is unreachable, add a navigation button on another screen that leads to it, or delete it.`
        );
      }
    });
  }

  // Validate routing rules
  const screenIdSet = new Set(screens.map(s => s.id));
  screens.forEach(screen => {
    const routing = screen.routing;
    if (!routing?.rules?.length) return;
    const screenLabel = screen.title || screen.id;

    // Up to MAX_IF_DEPTH rules compile to an If chain; more compile to a Switch on the answer,
    // which needs every rule to test one required single-choice field with fixed options on this
    // screen. Meta refuses a deeper chain, so refuse it here first.
    const compilable = routing.rules.filter(rule => rule && rule.nextScreenId
      && (rule.mode === 'power' ? !!rule.expression?.trim() : !!(rule.fieldName && rule.operator)));
    if (compilable.length > MAX_IF_DEPTH && !switchRouting(screen)) {
      errors.push(
        `Screen "${screenLabel}" has ${compilable.length} routing rules; WhatsApp nests conditions only ${MAX_IF_DEPTH} deep, so more than ${MAX_IF_DEPTH} rules must all test one required RadioButtonsGroup or Dropdown with fixed options on this screen ("equals" an option).`
      );
    }
    routing.rules.forEach((rule, idx) => {
      if (rule?.mode === 'power' && COMBINED_COMPARISON.test(rule.expression || '')) {
        errors.push(`Screen "${screenLabel}" routing rule ${idx + 1} combines two comparisons with || or &&, which WhatsApp cannot parse. One comparison per rule.`);
      }
    });

    routing.rules.forEach((rule, idx) => {
      const hasCondition = rule.mode === 'power'
        ? !!rule.expression?.trim()
        : !!(rule.fieldName && rule.operator);

      if (!hasCondition) {
        warnings.push(
          `Screen "${screenLabel}" routing rule ${idx + 1} has no condition set.`
        );
      }

      if (!rule.nextScreenId) {
        warnings.push(
          `Screen "${screenLabel}" routing rule ${idx + 1} has no target screen selected.`
        );
      } else if (rule.nextScreenId === screen.id) {
        // Meta: "Same screen navigation is not allowed. Loop detected" (2026-09-18).
        errors.push(
          `Screen "${screenLabel}" routing rule ${idx + 1} sends the customer back to the same screen, which WhatsApp does not allow. An answer that ends the flow with a message needs its own final screen.`
        );
      } else if (!screenIdSet.has(rule.nextScreenId)) {
        errors.push(
          `Screen "${screenLabel}" routing rule ${idx + 1} targets an unknown screen.`
        );
      }
    });
  });

  return { errors, warnings };
};

export const validateBuilderScreens = (screens) => {
  const errors = [];
  const warnings = [];

  if (!screens || !Array.isArray(screens)) {
    errors.push('Invalid screens data');
    return { errors, warnings };
  }

  const carouselCount = countImageCarouselsInFlow(screens);
  if (carouselCount > MAX_IMAGE_CAROUSELS_PER_FLOW) {
    errors.push(
      `Flow has ${carouselCount} ImageCarousel components, WhatsApp allows at most ${MAX_IMAGE_CAROUSELS_PER_FLOW} per flow.`
    );
  }

  screens.forEach((screen, index) => {
    const screenLabel = screen.title || `Screen ${index + 1}`;

    if (!screen.id) {
      errors.push(`Screen ${index + 1} is missing an ID`);
    }

    if (!screen.title) {
      warnings.push(`Screen ${index + 1} is missing a title`);
    }

    if (!screen.components || !Array.isArray(screen.components)) {
      errors.push(`Screen ${index + 1} has invalid components`);
      return;
    }

    // BUG-004: Block export for screens that have no content components.
    // A Footer alone does not count as content, Meta rejects screens with
    // an empty children array.
    const NAVIGATION_ONLY_TYPES = new Set(['Footer']);
    const contentComponents = screen.components.filter(
      c => !NAVIGATION_ONLY_TYPES.has(c.type)
    );
    if (contentComponents.length === 0) {
      errors.push(
        `Screen "${screenLabel}" has no components, add at least one field or text block before publishing.`
      );
    }

    // WhatsApp caps a screen's layout children. Counted on the top-level
    // components because that is what the layout ends up holding, a container
    // (If / Switch) serializes to a single child regardless of its contents.
    if (screen.components.length > MAX_COMPONENTS_PER_SCREEN) {
      errors.push(
        `Screen "${screenLabel}" has too many components (max ${MAX_COMPONENTS_PER_SCREEN})`
      );
    }

    // Check component limits
    const componentCounts = {};
    screen.components.forEach(comp => {
      componentCounts[comp.type] = (componentCounts[comp.type] || 0) + 1;
    });

    Object.entries(componentCounts).forEach(([type, count]) => {
      const maxPerScreen = getComponentMaxPerScreen(type);
      if (maxPerScreen && count > maxPerScreen) {
        errors.push(`Screen "${screenLabel}" has too many ${type} components (max ${maxPerScreen})`);
      }
    });
    if (richTextShareViolation(screen.components)) {
      errors.push(`Screen "${screenLabel}", ${RICH_TEXT_ALONE_ERROR}`);
    }

    // BUG-001: Run per-component property validation so empty labels,
    // zero-option dropdowns, etc. are caught before export/publish.
    // Recurses into container branches (shared walker) so a broken component
    // nested in an If/Switch blocks the save gate too, not just the inline map.
    screen.components.forEach(topLevel => {
      forEachComponentDeep(topLevel, (component) => {
        const compValidation = validateComponent(component, { screens });
        compValidation.errors.forEach(err => {
          errors.push(`Screen "${screenLabel}", ${err}`);
        });
        compValidation.warnings.forEach(warn => {
          warnings.push(`Screen "${screenLabel}", ${warn}`);
        });
      });
    });

    // Navigation validation
    if (screens.length > 1) {
      const isLastScreen = index === screens.length - 1;
      const hasFooter = screen.components.some(comp => comp.type === 'Footer');

      if (!isLastScreen && !hasFooter) {
        warnings.push(`Screen "${screenLabel}" needs a Footer component to proceed to the next screen`);
      }

      if (isLastScreen && !hasFooter) {
        warnings.push(`Terminal screen "${screenLabel}" needs a Footer component to end the flow`);
      }
    }
  });

  // Flow-level checks (reachability, routing rules), shared with validateScreenGraph so the
  // builders can show them too. Same findings, same order as before they were extracted.
  const structure = validateScreenGraph(screens);
  errors.push(...structure.errors);
  warnings.push(...structure.warnings);

  return { errors, warnings };
};

/**
 * Per-component / per-screen validation for inline display in the builder.
 *
 * validateBuilderScreens returns a flat list of error strings prefixed with the
 * screen label, great for snackbars and pre-deploy gates, but it can't be
 * surfaced inline because the strings have lost the component id.
 *
 * This walks the same screens and returns:
 *   {
 *     byComponent: Map<componentId, { errors, warnings, screenId, componentType }>,
 *     byScreen:    Map<screenId,    { errors, warnings }>,
 *     totals:      { errorCount, warningCount }
 *   }
 *
 * Only checks that can be tied to a specific component or screen are placed
 * here; flow-level checks (reachability, routing) are validateScreenGraph's, which the
 * builders show alongside this map.
 */
export const buildValidationMap = (screens, options = {}) => {
  const { hasEndpoint = false } = options;
  const byComponent = new Map();
  const byScreen = new Map();
  let errorCount = 0;
  let warningCount = 0;

  if (!Array.isArray(screens)) {
    return { byComponent, byScreen, totals: { errorCount, warningCount } };
  }

  const NAVIGATION_ONLY_TYPES = new Set(['Footer']);

  // Cross-cutting check: a data-exchange action on any component requires the
  // flow's live data to have somewhere to go (the flow's Connections).
  // Without one, Meta would call an endpoint that was never registered, so the
  // flow can never complete. We surface this per-component so the builder can
  // highlight the offending Footer / NavigationList inline.
  let dataExchangeSeen = false;
  const isDataExchangeAction = (action) => action?.name === 'data_exchange';
  const componentHasDataExchange = (component) => {
    if (!component) return false;
    const props = component.properties || {};
    if (isDataExchangeAction(props['on-click-action'])) return true;
    // NavigationList carries per-item actions rather than a single
    // on-click-action.
    const listItems = props['list-items'] || [];
    return listItems.some((item) => isDataExchangeAction(item['on-click-action']));
  };

  // First pass: does ANY component anywhere in the flow (including container
  // branches) perform a data_exchange? Dynamic data-source bindings need this,
  // the list is populated by a data_exchange response, so without one the flow
  // can never load its options.
  const componentUsesDynamicDataSource = (component) =>
    component?.properties?.['data-source-mode'] === 'dynamic';

  const flowHasDataExchange = (() => {
    let found = false;
    forEachComponentInScreens(screens, (component) => {
      if (componentHasDataExchange(component)) found = true;
    });
    return found;
  })();

  screens.forEach((screen, index) => {
    const screenLabel = screen.title || `Screen ${index + 1}`;
    const screenErrors = [];
    const screenWarnings = [];

    if (!screen.title) {
      screenWarnings.push('Screen is missing a title');
    }

    if (Array.isArray(screen.components)) {
      const contentComponents = screen.components.filter(
        (c) => !NAVIGATION_ONLY_TYPES.has(c.type),
      );
      if (contentComponents.length === 0) {
        screenErrors.push(
          'Screen has no components, add at least one field or text block before publishing.',
        );
      }

      if (screen.components.length > MAX_COMPONENTS_PER_SCREEN) {
        screenErrors.push(`Too many components (max ${MAX_COMPONENTS_PER_SCREEN})`);
      }

      const componentCounts = {};
      screen.components.forEach((comp) => {
        componentCounts[comp.type] = (componentCounts[comp.type] || 0) + 1;
      });
      Object.entries(componentCounts).forEach(([type, count]) => {
        const maxPerScreen = getComponentMaxPerScreen(type);
        if (maxPerScreen && count > maxPerScreen) {
          screenErrors.push(`Too many ${type} components (max ${maxPerScreen})`);
        }
      });

      // Walks top-level components AND container branches (If.then/else,
      // Switch.cases.*) via the shared walker. validateComponent on If/Switch
      // only checks the container's own properties, so without this recursion
      // a Dropdown with zero options nested in an If's then-branch would pass
      // the inline checks but still fail at deploy time.
      const visit = (component) => {
        const compValidation = validateComponent(component, { screens });
        const compErrors = compValidation.errors || [];
        const compWarnings = compValidation.warnings || [];

        // Dynamic-flow cross-check: a data-exchange action without a
        // configured endpoint is a hard error.
        if (componentHasDataExchange(component)) {
          dataExchangeSeen = true;
          if (!hasEndpoint) {
            compErrors.push(
              'This component asks for live data, but the flow has nowhere to get it from. Save the flow, then connect your server in its Connections.',
            );
          }
        }

        // Dynamic data-source cross-check: a list populated by a data_exchange
        // response is meaningless unless some screen actually performs a data
        // exchange to fetch those options.
        if (componentUsesDynamicDataSource(component) && !flowHasDataExchange) {
          compErrors.push(
            'This list is populated by a data_exchange response, but no screen performs a data exchange.',
          );
        }

        if (compErrors.length || compWarnings.length) {
          byComponent.set(component.id, {
            errors: compErrors,
            warnings: compWarnings,
            screenId: screen.id,
            screenLabel,
            componentType: component.type,
          });
          errorCount += compErrors.length;
          warningCount += compWarnings.length;
        }
      };

      screen.components.forEach((component) => forEachComponentDeep(component, visit));
    }

    if (screenErrors.length || screenWarnings.length) {
      byScreen.set(screen.id, {
        errors: screenErrors,
        warnings: screenWarnings,
        screenLabel,
      });
      errorCount += screenErrors.length;
      warningCount += screenWarnings.length;
    }
  });

  // Flow-level: WhatsApp caps ImageCarousel at 3 per flow. Keyed to the first
  // screen so the panel can show it (the limit is global, not per-screen).
  const carouselCount = countImageCarouselsInFlow(screens);
  if (carouselCount > MAX_IMAGE_CAROUSELS_PER_FLOW && screens.length > 0) {
    const firstScreen = screens[0];
    const existing = byScreen.get(firstScreen.id) || {
      errors: [],
      warnings: [],
      screenLabel: firstScreen.title || 'Screen 1',
    };
    existing.errors = [
      ...existing.errors,
      `Flow has ${carouselCount} ImageCarousel components, WhatsApp allows at most ${MAX_IMAGE_CAROUSELS_PER_FLOW} per flow.`,
    ];
    byScreen.set(firstScreen.id, existing);
    errorCount += 1;
  }

  // Flow-level: an endpoint is configured but nothing actually exchanges data
  // with it. Not fatal, but almost certainly a mistake, surface as a warning
  // keyed to the first screen so the panel can show it.
  if (hasEndpoint && !dataExchangeSeen && screens.length > 0) {
    const firstScreen = screens[0];
    const existing = byScreen.get(firstScreen.id) || {
      errors: [],
      warnings: [],
      screenLabel: firstScreen.title || 'Screen 1',
    };
    existing.warnings = [
      ...existing.warnings,
      'A data endpoint is configured but no screen uses a data-exchange action.',
    ];
    byScreen.set(firstScreen.id, existing);
    warningCount += 1;
  }

  return { byComponent, byScreen, totals: { errorCount, warningCount } };
};